/**
 * Homophone tolerance for the read-aloud games.
 *
 * Speech recognition transcribes sound, not spelling — a kid who correctly reads
 * "two" gets back "too" or "2" about as often as not. Marking that wrong teaches
 * them the opposite of what happened, so any word that is pronounced the same as
 * the target counts as correct.
 *
 * Each line is one pronunciation group. Membership is symmetric: saying any
 * member of a group satisfies any other member of it.
 */
const GROUPS: string[][] = [
  // The ones that actually bite in early reading
  ['to', 'too', 'two', '2'],
  ['there', 'their', 'theyre'],
  ['your', 'youre'],
  ['no', 'know'],
  ['one', 'won', '1'],
  ['for', 'four', 'fore', '4'],
  ['ate', 'eight', '8'],
  ['be', 'bee'],
  ['see', 'sea'],
  ['hear', 'here'],
  ['knew', 'new'],
  ['by', 'buy', 'bye'],
  ['our', 'hour'],
  ['week', 'weak'],
  ['wood', 'would'],
  ['way', 'weigh'],
  ['wait', 'weight'],
  ['son', 'sun'],
  ['blue', 'blew'],
  ['road', 'rode', 'rowed'],
  ['right', 'write', 'rite', 'wright'],
  ['tail', 'tale'],
  ['meet', 'meat'],
  ['deer', 'dear'],
  ['hair', 'hare'],
  ['bear', 'bare'],
  ['pair', 'pear', 'pare'],
  ['plain', 'plane'],
  ['rain', 'reign', 'rein'],
  ['sale', 'sail'],
  ['some', 'sum'],
  ['break', 'brake'],
  ['cent', 'scent', 'sent'],
  ['made', 'maid'],
  ['mail', 'male'],
  ['main', 'mane'],
  ['night', 'knight'],
  ['not', 'knot'],
  ['peace', 'piece'],
  ['steal', 'steel'],
  ['threw', 'through'],
  ['toe', 'tow'],
  ['whole', 'hole'],
  ['wear', 'where', 'ware'],
  ['weather', 'whether'],
  ['which', 'witch'],
  ['allowed', 'aloud'],
  ['ant', 'aunt'],
  ['ball', 'bawl'],
  ['board', 'bored'],
  ['cell', 'sell'],
  ['chews', 'choose'],
  ['close', 'clothes'],
  ['die', 'dye'],
  ['eye', 'i'],
  ['fair', 'fare'],
  ['feat', 'feet'],
  ['find', 'fined'],
  ['flea', 'flee'],
  ['flour', 'flower'],
  ['grate', 'great'],
  ['groan', 'grown'],
  ['heal', 'heel'],
  ['heard', 'herd'],
  ['hi', 'high'],
  ['in', 'inn'],
  ['lead', 'led'],
  ['loan', 'lone'],
  ['might', 'mite'],
  ['missed', 'mist'],
  ['oar', 'or', 'ore'],
  ['pail', 'pale'],
  ['pause', 'paws'],
  ['poor', 'pour', 'pore'],
  ['real', 'reel'],
  ['root', 'route'],
  ['rose', 'rows'],
  ['sew', 'so', 'sow'],
  ['side', 'sighed'],
  ['sight', 'site', 'cite'],
  ['stair', 'stare'],
  ['tea', 'tee'],
  ['thrown', 'throne'],
  ['tide', 'tied'],
  ['toad', 'towed'],
  ['vain', 'vane', 'vein'],
  ['waist', 'waste'],
  ['war', 'wore'],
  ['wine', 'whine'],
  ['bald', 'bawled'],
  ['berry', 'bury'],
  ['cheap', 'cheep'],
  ['dew', 'do', 'due'],
  ['flew', 'flu', 'flue'],
  ['gate', 'gait'],
  ['guessed', 'guest'],
  ['hymn', 'him'],
  ['mall', 'maul'],
  ['nose', 'knows'],
  ['pea', 'pee'],
  ['read', 'reed'],
  ['ring', 'wring'],
  ['scene', 'seen'],
  ['seam', 'seem'],
  ['sole', 'soul'],
  ['stake', 'steak'],
  ['wave', 'waive'],
  ['whos', 'whose'],
  ['band', 'banned'],
  ['bread', 'bred'],
  ['course', 'coarse'],
  ['knead', 'need'],
  ['pane', 'pain'],
  ['seas', 'sees', 'seize'],
  ['three', '3'],
  ['five', '5'],
  ['six', '6'],
  ['seven', '7'],
  ['nine', '9'],
  ['ten', '10'],
]

/** word -> id of the group it belongs to. */
const GROUP_OF = new Map<string, number>()
GROUPS.forEach((group, i) => {
  for (const word of group) {
    // First group wins if a word somehow appears twice; keeps lookup total.
    if (!GROUP_OF.has(word)) GROUP_OF.set(word, i)
  }
})

/** Lowercase, strip everything that isn't a letter or digit. */
export function normalizeSpoken(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * True when what the kid said should be accepted for the word on screen —
 * either an exact match or a homophone of it.
 */
export function spokenMatches(spoken: string, expected: string): boolean {
  const a = normalizeSpoken(spoken)
  const b = normalizeSpoken(expected)
  if (!a || !b) return false
  if (a === b) return true

  const groupA = GROUP_OF.get(a)
  const groupB = GROUP_OF.get(b)
  return groupA !== undefined && groupA === groupB
}

/** True when the two differ in spelling but sound the same — worth remarking on. */
export function isHomophoneMatch(spoken: string, expected: string): boolean {
  const a = normalizeSpoken(spoken)
  const b = normalizeSpoken(expected)
  return a !== b && spokenMatches(a, b)
}
